import { createContext, ReactNode, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project-imports
import Loader from 'components/Loader';
import { authLogout, fetchMe } from 'api';
import { LOGIN_PATH } from 'config';
import { isOAuthCallbackPath } from 'utils/return-to';

// types
import { AuthContextType, AuthProps } from 'types/auth';
import { MeResponse, Permission } from 'types/console';

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// ==============================|| AUTH CONTEXT & PROVIDER ||============================== //
//
// Who the admin is and what they may see. There is no token in here: the session is an HttpOnly
// cookie, and `/v1/me` is the only source of truth. The permissions array drives presentation (which
// menu items and buttons appear); every API call is still checked on the server.

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [me, setMe] = useState<MeResponse | null>(null);

  const completeLogin = useCallback((next: MeResponse) => {
    setMe(next);
    dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: next.user } });
  }, []);

  const dropSession = useCallback(() => {
    setMe(null);
    dispatch({ type: LOGOUT });
  }, []);

  // Start-up check. An expired access token is refreshed by the axios layer before this fails, so a
  // rejection here means there is no session at all.
  useEffect(() => {
    // On the OAuth callback pages the callback request creates the session. Probing `/v1/me` there races
    // it: the probe's refresh can expire the CSRF cookie the callback POST needs. The callback page hands
    // its payload to completeLogin() instead, so the start-up state is simply "not signed in" here.
    if (isOAuthCallbackPath(window.location.pathname)) {
      dropSession();

      return;
    }

    let active = true;

    fetchMe()
      .then((next) => {
        if (active) completeLogin(next);
      })
      .catch(() => {
        if (active) dropSession();
      });

    return () => {
      active = false;
    };
    // The start-up probe runs once, when the provider mounts; navigating later does not repeat it.
  }, [completeLogin, dropSession]);

  const refreshMe = useCallback(async () => {
    completeLogin(await fetchMe());
  }, [completeLogin]);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch {
      // The session is going away locally either way; a failed revoke is not worth blocking on.
    }

    dropSession();

    // A full navigation discards every piece of client state, not only the auth state. The next
    // person to sign in on this browser must not inherit the previous admin's open pages.
    window.location.assign(LOGIN_PATH);
  }, [dropSession]);

  const can = useCallback((permission: Permission) => me?.permissions.includes(permission) ?? false, [me]);

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      me,
      isPlatform: me?.platformRole != null,
      can,
      completeLogin,
      refreshMe,
      logout
    }),
    [state, me, can, completeLogin, refreshMe, logout]
  );

  // Nothing renders until the session is known, so no guard decides against a half-loaded state.
  if (!state.isInitialized) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
